/**
 * What a lead source IS, and what to call it.
 *
 * `contacts.lead_source` is one free-text column doing two jobs. Sometimes it
 * holds a name a person chose from a picker (`Referral`, `Walk-in`), and
 * sometimes it holds the page the registration form was submitted from —
 * which, for anything driven by an ad, is a URL with the whole campaign
 * stapled to it:
 *
 *   https://bogarthills.com/?utm_source=meta&utm_medium=paid-social
 *     &utm_campaign=Community+x+Bogart+Hills+%7C+Prospecting+%7C+Lead-Gen+Campaign
 *     &utm_content=Downsizers+v2&utm_term=Ad+1&fbclid=IwcGRvZgFmZGl
 *
 * Rendered as a label that is 200 characters of query string, and grouped in
 * the breakdown chart as its own bar — one bar per ad variant, because every
 * `utm_content` makes the string unique.
 *
 * So this module answers the two questions separately:
 *
 *   name  — "Meta — Paid Social". Short, groupable, the same for every ad in
 *           the campaign. THIS is what a chart groups by and a badge shows.
 *   url   — the page itself, kept whole for the person who wants it.
 *
 * The name is derived from the campaign markers the ad platforms themselves
 * put there: `utm_source`/`utm_medium` when present, otherwise the click id
 * every network appends (`fbclid`, `gclid`, `msclkid`, …), otherwise the host
 * when it is somewhere recognisable, otherwise "Website" — a registration from
 * a page carrying no campaign marker at all is a website registration.
 *
 * WHY NO MODEL CALL: every one of those markers is a documented, stable
 * contract published by the platform that writes it. Naming them is a lookup,
 * and a lookup is instant, free, offline, and identical every time it runs —
 * which matters here because the name is a GROUPING KEY. A namer that can
 * answer "Meta" today and "Facebook Ads" tomorrow splits one bar into two.
 */

/**
 * The wording a surface supplies for the values the app names itself.
 *
 * `site` is the project's own name, and it is what turns a bare "Website" into
 * "Bogart Hills Website". It has to reach BOTH the `website` label and a URL on
 * the project's own domain, or the breakdown chart splits one thing into two
 * bars again — which is the bug this whole module exists to close.
 */
export interface LeadSourceLabels {
  website?: string;
  manual?: string;
  site?: string | null;
}

/** The two names the app writes itself, and what a parsed source looks like. */
export interface ParsedLeadSource {
  /** Short, human, and stable enough to group by. */
  name: string;
  /** The page the contact registered from, normalised. Null when not a URL. */
  url: string | null;
  /** Host + path, for a preview that fits: the query lives in the tooltip. */
  display: string | null;
  /** `utm_campaign`, decoded. */
  campaign: string | null;
  /** `utm_content` — usually the ad variant. */
  content: string | null;
  /** `utm_term` — usually the ad or keyword. */
  term: string | null;
  /** How the name was reached. Nothing branches on it; it explains the name. */
  origin: 'label' | 'utm' | 'click-id' | 'referrer' | 'site';
}

/** Stored spellings that mean "registered through the website". */
const WEBSITE_ALIASES = new Set(['website', 'public_form', 'public form', 'web']);

/** Stored spellings that mean "somebody typed this contact in". */
const MANUAL_ALIASES = new Set(['manual', 'manually entered', 'console']);

/** `utm_source` values the platforms write, and what people call them. */
const SOURCE_NAMES: Record<string, string> = {
  meta: 'Meta',
  facebook: 'Meta',
  fb: 'Meta',
  'facebook.com': 'Meta',
  instagram: 'Instagram',
  ig: 'Instagram',
  // Meta's {{site_source_name}} for an ad shown on Threads (freure.com, 2026-10).
  threads: 'Threads',
  th: 'Threads',
  google: 'Google',
  adwords: 'Google Ads',
  googleads: 'Google Ads',
  youtube: 'YouTube',
  bing: 'Microsoft',
  microsoft: 'Microsoft',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  twitter: 'X',
  x: 'X',
  reddit: 'Reddit',
  snapchat: 'Snapchat',
  mailchimp: 'Email',
  klaviyo: 'Email',
  sendgrid: 'Email',
  newsletter: 'Newsletter',
  email: 'Email',
};

/** `utm_medium` values, likewise. */
const MEDIUM_NAMES: Record<string, string> = {
  cpc: 'Paid Search',
  ppc: 'Paid Search',
  paidsearch: 'Paid Search',
  'paid-search': 'Paid Search',
  'paid search': 'Paid Search',
  paidsocial: 'Paid Social',
  'paid-social': 'Paid Social',
  'paid social': 'Paid Social',
  social: 'Social',
  display: 'Display',
  banner: 'Display',
  video: 'Video',
  email: 'Email',
  organic: 'Organic',
  referral: 'Referral',
  affiliate: 'Affiliate',
  sms: 'SMS',
  retargeting: 'Retargeting',
  remarketing: 'Retargeting',
};

/**
 * Click ids. A network appends one to every outbound click, so it identifies
 * the source even when whoever built the ad forgot the UTM parameters —
 * which, in practice, is most of the time.
 */
const CLICK_IDS: Record<string, string> = {
  fbclid: 'Meta',
  igshid: 'Instagram',
  gclid: 'Google Ads',
  gbraid: 'Google Ads',
  wbraid: 'Google Ads',
  dclid: 'Google Display',
  msclkid: 'Microsoft Ads',
  ttclid: 'TikTok',
  li_fat_id: 'LinkedIn',
  twclid: 'X',
  epik: 'Pinterest',
  rdt_cid: 'Reddit',
  sccid: 'Snapchat',
};

/**
 * Hosts worth naming when a registration arrives from somewhere that is not
 * the project's own site. Matched on the registrable domain, so
 * `www.realtor.ca` and `m.facebook.com` both land.
 */
const REFERRER_HOSTS: Record<string, string> = {
  'facebook.com': 'Meta',
  'instagram.com': 'Instagram',
  'google.com': 'Google',
  'google.ca': 'Google',
  'bing.com': 'Microsoft',
  'youtube.com': 'YouTube',
  'tiktok.com': 'TikTok',
  'linkedin.com': 'LinkedIn',
  'pinterest.com': 'Pinterest',
  'twitter.com': 'X',
  'x.com': 'X',
  'reddit.com': 'Reddit',
  'realtor.ca': 'Realtor.ca',
  'zillow.com': 'Zillow',
  'kijiji.ca': 'Kijiji',
  'condos.ca': 'Condos.ca',
  'buzzbuzzhome.com': 'BuzzBuzzHome',
  'livabl.com': 'Livabl',
  'point2homes.com': 'Point2Homes',
  'newinhomes.com': 'NewInHomes',
};

/**
 * Is this a link rather than a word? Either an explicit scheme, a `www.` host,
 * or a bare `host.tld` with no spaces in it. A label like `Google Ads` has a
 * space and no dot; `Walk-in` has neither — neither is caught here.
 */
function isUrlLike(value: string): boolean {
  if (/\s/.test(value)) return false;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return true;
  if (/^www\./i.test(value)) return true;
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+([/?#].*)?$/i.test(value);
}

/** Title-case a token nobody has a name for: `acme-partners` → Acme Partners. */
function titleCase(value: string): string {
  return value
    .replace(/[_+]/g, ' ')
    .split(/\s+|-/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** The last two labels of a host, so subdomains do not defeat the lookup. */
function registrableDomain(hostname: string): string {
  const parts = hostname.toLowerCase().replace(/^www\./, '').split('.');
  if (parts.length <= 2) return parts.join('.');
  // Handles co.uk / com.au style suffixes well enough for a display name.
  const tail = parts.slice(-2).join('.');
  if (/^(co|com|net|org|gov|ac)\.[a-z]{2}$/.test(tail)) return parts.slice(-3).join('.');
  return tail;
}

/** Accents and punctuation removed, for comparing a name against a domain. */
function flatten(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * The project's name, trimmed at the first separator: "Bogart Hills — Phase II
 * (Tour Nord)" is one website, and the phase is not part of its name.
 */
function siteLabel(projectName: string | null | undefined): string | null {
  if (!projectName) return null;
  const trimmed = projectName.split(/\s+[—–|]\s+|\s+\(/)[0].trim();
  return trimmed || null;
}

/**
 * Does this host belong to the project itself?
 *
 * Compared by squashing both sides to letters and digits and looking for any
 * run of the project's words inside the domain label — `bogarthills.com` is
 * "Bogart Hills", and `parclafontaine.ca` is "Les Résidences du Parc
 * Lafontaine". Without a project name there is nothing to compare against, so
 * the answer is "assume so": a registration form is embedded on the project's
 * own site far more often than anywhere else, and that was the behaviour
 * before any of this existed.
 */
function isProjectSite(host: string, projectName: string | null | undefined): boolean {
  if (!projectName) return true;
  const label = flatten(registrableDomain(host).split('.')[0]);
  if (!label) return false;
  const words = projectName.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  for (let start = 0; start < words.length; start++) {
    for (let end = start + 1; end <= words.length; end++) {
      const run = flatten(words.slice(start, end).join(''));
      if (!run) continue;
      // Exact, or a long enough prefix that `bogarthillshomes.com` still reads
      // as the project's site. The length floor is what stops a short leading
      // word matching a stranger: "Victoria Highlands" must not claim
      // `victoriasecret.com`.
      if (run === label || (run.length >= 10 && label.startsWith(run))) return true;
    }
  }
  return false;
}

function paramName(map: Record<string, string>, raw: string | null): string | null {
  if (!raw) return null;
  const key = raw.trim().toLowerCase();
  if (!key) return null;
  return map[key] ?? titleCase(key);
}

/**
 * Parse a stored lead source into a name, a URL, and the campaign detail.
 *
 * `labels` supplies translated wording for the two values the app names
 * itself; everything else is either the stored text or derived from the URL,
 * and is not translated — a campaign name is a proper noun.
 */
export function parseLeadSource(
  raw: string | null | undefined,
  labels: LeadSourceLabels = {},
): ParsedLeadSource {
  const site = siteLabel(labels.site);
  const websiteWord = labels.website || 'Website';
  // "Bogart Hills Website" — a builder runs several projects and each has its
  // own site, so "Website" alone names nothing in particular.
  // Spelled out when no wording was supplied: that English is a server-text
  // sentence the client recognises (shared/serverText/dashboard.ts).
  const websiteLabel = site
    ? labels.website ? `${site} ${labels.website}` : `${site} Website`
    : websiteWord;
  const manualLabel = labels.manual || 'Manually Entered';

  const blank: Omit<ParsedLeadSource, 'name' | 'origin'> = {
    url: null,
    display: null,
    campaign: null,
    content: null,
    term: null,
  };

  const value = (raw || '').trim();
  if (!value) return { ...blank, name: manualLabel, origin: 'label' };

  const key = value.toLowerCase();
  if (WEBSITE_ALIASES.has(key)) return { ...blank, name: websiteLabel, origin: 'label' };
  if (MANUAL_ALIASES.has(key)) return { ...blank, name: manualLabel, origin: 'label' };

  if (!isUrlLike(value)) {
    const spaced = value.replace(/_/g, ' ');
    return {
      ...blank,
      name: spaced.charAt(0).toUpperCase() + spaced.slice(1),
      origin: 'label',
    };
  }

  let parsed: URL;
  try {
    // A bare `bogarthills.com/x` has no scheme; assume https rather than
    // letting `new URL` throw and losing the URL half of the answer.
    parsed = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return { ...blank, name: value, origin: 'label' };
  }

  const params = parsed.searchParams;
  // `href` lowercases the scheme and host for us — which is the whole of the
  // "Https://bogarthills.com/" fix, wherever the capital came from.
  const url = parsed.href;
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');
  const display = `${host}${path}`;

  const campaign = params.get('utm_campaign')?.trim() || null;
  const content = params.get('utm_content')?.trim() || null;
  const term = params.get('utm_term')?.trim() || null;

  const source = paramName(SOURCE_NAMES, params.get('utm_source'));
  const medium = paramName(MEDIUM_NAMES, params.get('utm_medium'));

  const detail = { url, display, campaign, content, term };

  if (source) {
    return {
      ...detail,
      name: medium && medium !== source ? `${source} — ${medium}` : source,
      origin: 'utm',
    };
  }

  for (const [param, network] of Object.entries(CLICK_IDS)) {
    if (params.has(param)) {
      return { ...detail, name: medium ? `${network} — ${medium}` : network, origin: 'click-id' };
    }
  }

  const referrer = REFERRER_HOSTS[registrableDomain(host)];
  if (referrer) return { ...detail, name: referrer, origin: 'referrer' };

  // No campaign marker of any kind: they were on a site and they registered.
  // Which site decides the name — the project's own is "<project> Website",
  // and anywhere else is named by its domain rather than absorbed into it.
  if (isProjectSite(host, labels.site)) {
    return { ...detail, name: websiteLabel, origin: 'site' };
  }
  return { ...detail, name: registrableDomain(host), origin: 'referrer' };
}

/**
 * Just the name — for a chart axis, a CSV cell, or anywhere a single string is
 * all there is room for. Surfaces with room should render the URL too; see
 * `LeadSourceValue`.
 */
export function formatLeadSource(
  raw: string | null | undefined,
  labels: LeadSourceLabels = {},
): string {
  return parseLeadSource(raw, labels).name;
}

/**
 * Collapse raw `lead_source` values onto the names the app shows.
 *
 * Two things made the breakdown chart lie. `Website` and `website` were
 * separate bars, and an ad-driven registration stores the landing page URL —
 * so every ad variant, keyed by its own `utm_content`, got a bar of one.
 * Grouping by the displayed name puts a campaign back together and merges the
 * spellings, which is what someone reading the chart already assumed it did.
 */
export function mergeLeadSources(
  entries: Array<[string, number]>,
  labels: LeadSourceLabels = {},
): Array<{ name: string; count: number; percentage: number }> {
  const merged = new Map<string, number>();
  for (const [raw, count] of entries) {
    const name = formatLeadSource(raw, labels);
    merged.set(name, (merged.get(name) || 0) + count);
  }
  const total = [...merged.values()].reduce((sum, count) => sum + count, 0);
  return [...merged.entries()]
    .map(([name, count]) => ({
      name,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0,
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * The option list for a Source picker or filter: raw values in, names out.
 *
 * Order is preserved — the endpoint returns them commonest-first — and names
 * are de-duplicated after naming, which is the entire point: three Meta ad
 * URLs and the word "website" are four rows in the database and two options a
 * person would recognise.
 *
 * `includeUnset` keeps the bucket for contacts with no source recorded. A
 * filter wants it ("who arrived without one?"); a picker does not — that name
 * is what is left when nobody chose anything, not something to choose.
 */
export function leadSourceOptions(
  values: Array<string | null | undefined>,
  labels: LeadSourceLabels = {},
  { includeUnset = false }: { includeUnset?: boolean } = {},
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    if (!value?.trim() && !includeUnset) continue;
    const name = formatLeadSource(value, labels);
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}

/** What a campaign's two free-form UTM fields hold, named for the reader. */
export interface CampaignFieldLabels {
  /** `utm_content`. */
  content: string;
  /** Translation key for `content` (the client resolves `t(contentKey, content)`). */
  contentKey: string;
  /** `utm_term`. */
  term: string;
  /** Translation key for `term`. */
  termKey: string;
}

/**
 * Name `utm_content` and `utm_term` for the platform that wrote them.
 *
 * The two parameters have no fixed meaning: each ad platform's tracking
 * template fills them with its own levels. The Meta links SaleFish projects
 * actually receive carry the ad set in `utm_content` ("Social Media
 * Retargeting", "Downsizers v2" — audiences) and the ad in `utm_term` ("Ad 5"),
 * the convention Meta's own URL builder suggests. Paid search uses
 * `utm_term` for the keyword that was bid on and `utm_content` for the ad.
 * Anywhere else the honest label is the parameter's own name.
 *
 * Read from the URL rather than from the display name, because the name is
 * translated and grouped while the parameters are the platform's contract.
 */
export function campaignFieldLabels(parsed: Pick<ParsedLeadSource, 'url'>): CampaignFieldLabels {
  const generic = { content: 'Content', contentKey: 'ctcCampaignFieldContent', term: 'Term', termKey: 'ctcCampaignFieldTerm' };
  if (!parsed.url) return generic;
  let params: URLSearchParams;
  try {
    params = new URL(parsed.url).searchParams;
  } catch {
    return generic;
  }

  const source = (params.get('utm_source') || '').trim().toLowerCase();
  const medium = (params.get('utm_medium') || '').trim().toLowerCase();
  const platform =
    SOURCE_NAMES[source] ??
    Object.entries(CLICK_IDS).find(([param]) => params.has(param))?.[1] ??
    null;

  if (platform === 'Meta' || platform === 'Instagram') {
    return { content: 'Ad Set', contentKey: 'ctcCampaignFieldAdSet', term: 'Ad', termKey: 'ctcCampaignFieldAd' };
  }
  const paidSearch = MEDIUM_NAMES[medium] === 'Paid Search';
  const searchNetwork =
    platform === 'Google' || platform === 'Google Ads' || platform === 'Microsoft' || platform === 'Microsoft Ads';
  if (paidSearch || (searchNetwork && !medium)) {
    return { content: 'Ad', contentKey: 'ctcCampaignFieldAd', term: 'Keyword', termKey: 'ctcCampaignFieldKeyword' };
  }
  return generic;
}

/**
 * Whether a page address carries an ad's markers: a `utm_*` parameter or a
 * network's click id. This is how the registration form decides that the page
 * it is on, or the page embedding it, is worth sending as the lead's source —
 * an address without one says nothing the site's own name does not.
 */
export function hasCampaignMarkers(url: string | null | undefined): boolean {
  if (!url) return false;
  let params: URLSearchParams;
  try {
    params = new URL(url).searchParams;
  } catch {
    return false;
  }
  for (const key of params.keys()) {
    const k = key.toLowerCase();
    if (k.startsWith('utm_') || k in CLICK_IDS) return true;
  }
  return false;
}
